import { useEffect, useMemo, useRef } from 'react';
import { buildViewModel } from '@/model';
import type { AppProps } from '@/model/ctx';
import { modelStore, setModel, useModelState } from '@/store/store';
import { useDemoData } from '@/client/DataProvider';
import { createTimers } from './timers';

export function useViewModel(props: AppProps) {
  const S = useModelState();
  const data = useDemoData();
  const timersRef = useRef<ReturnType<typeof createTimers> | null>(null);
  if (!timersRef.current) timersRef.current = createTimers();
  useEffect(() => () => timersRef.current?.dispose(), []);
  const timers = timersRef.current;
  // The tooltip element, owned here so it outlives every re-render: `moveTip` writes `left`/`top` on it directly on
  // each pointer move, and a ref recreated per render would be pointing at nothing by the time the pointer moved.
  const tipRef = useRef<HTMLDivElement | null>(null);

  // The entrance ramp (§ the reference's `startIntro`): meters are drawn at `value × intro`, so figures grow from
  // zero on first paint instead of appearing at full length. Runs once, on mount.
  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Reduced motion lands on the real number immediately — not a faster animation, no animation.
    if (reduce) {
      setModel({ intro: 1 });
      return;
    }
    const duration = 1600;
    const started = performance.now();
    let frame = 0;
    let last = -1;
    const step = (now: number): void => {
      const p = Math.min(1, (now - started) / duration);
      const eased = 1 - Math.pow(1 - p, 2); // easeOutQuad — slow enough at the end to read the digits
      // Quantised to fortieths: ~40 re-renders over the ramp rather than one per frame, which for a whole-screen
      // view-model rebuild is the difference between a ramp and a stutter.
      const q = p < 1 ? Math.round(eased * 40) / 40 : 1;
      if (q !== last) {
        last = q;
        setModel({ intro: q });
      }
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, []);

  return useMemo(
    () => buildViewModel({ S, set: setModel, props, data, timers, tipRef }),
    [S, props, data, timers],
  );
}
export { modelStore };
